import { useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isErrorHandled, isUnexpectedApiError, notifyGlobalError } from "./globalErrors";

export class ApiError extends Error {
  constructor(message, status, data, requestId) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    // Backend request id (body.requestId or X-Request-Id header), useful to correlate logs
    this.requestId = requestId;
  }
}

/*
 * Builds a fetch wrapper for the backend API.
 * - Adds the Clerk bearer token.
 * - Parses JSON safely (empty / invalid bodies become {}).
 * - On 401 calls onUnauthorized() and resolves to null, so callers must `if (!data) return;`.
 * - On any other non-ok status throws ApiError(data.error || `HTTP ${status}`).
 * - Network failures throw ApiError with status 0.
 * - 5xx and network failures are also sent to the global error modal (notifyGlobalError)
 *   before being thrown, so existing catch/toast handling keeps working.
 */
export function createApiFetch({ getToken, onUnauthorized }) {
  return async function apiFetch(path, { method = "GET", body, signal } = {}) {
    let response;
    try {
      response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}${path}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      throw notifyIfUnexpected(new ApiError("Unable to reach the server", 0), method, path);
    }

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      onUnauthorized?.();
      return null;
    }
    if (!response.ok) {
      const requestId = data?.requestId || response.headers?.get?.("X-Request-Id") || undefined;
      throw notifyIfUnexpected(
        new ApiError(data?.error || `HTTP ${response.status}`, response.status, data, requestId),
        method,
        path
      );
    }
    return data ?? {};
  };
}

function notifyIfUnexpected(error, method, path) {
  if (isUnexpectedApiError(error)) {
    notifyGlobalError(error, {
      source: "api",
      status: error.status,
      requestId: error.requestId,
      extra: { method, path: String(path).split("?")[0] },
    });
  }
  return error;
}

export const SIGN_IN_PATH = "/sign-in";

// Several requests usually fail with 401 at once (e.g. on the first visit of the day): log out only once
let signingOut = false;

/*
 * Ends the Clerk session and sends the user to the login page. A 401 means the Clerk token or the
 * backend session (Upstash, 30 min TTL) has expired; a new login recreates both.
 */
export async function redirectToSignIn({ signOut, navigate }) {
  if (signingOut) return;
  signingOut = true;
  try {
    await signOut({ redirectUrl: SIGN_IN_PATH });
  } catch {
    navigate(SIGN_IN_PATH, { replace: true });
  } finally {
    signingOut = false;
  }
}

/* Hook version for components: 401 logs the user out and redirects to the login page */
export function useApiFetch() {
  const { getToken, signOut } = useAuth();
  const navigate = useNavigate();

  return useCallback(
    (path, options) =>
      createApiFetch({
        getToken,
        onUnauthorized: () => redirectToSignIn({ signOut, navigate }),
      })(path, options),
    [getToken, signOut, navigate]
  );
}

/* Message to show in a toast for any caught error */
export function errorMessage(error, fallback = "Something went wrong") {
  return error?.message || fallback;
}

/*
 * Error toast for a caught error. Skipped for errors already shown by the global error modal
 * (5xx / server unreachable), so an outage shows a single "Connecting to the server" modal
 * instead of one toast per failed request.
 */
export function toastError(error, fallback) {
  if (isErrorHandled(error)) return;
  toast.error(errorMessage(error, fallback));
}
